import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ChevronLeft, Clock, CheckCircle, XCircle, Truck, X, RotateCcw } from 'lucide-react';
import { orderService } from '../services/orderService';
import toast from 'react-hot-toast';

const MyOrders = ({ onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderService.getUserOrders();
      setOrders(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'Cancelled':
        return <XCircle className="text-red-500" size={18} />;
      case 'Shipped':
        return <Truck className="text-blue-500" size={18} />;
      case 'Processing':
        return <Clock className="text-yellow-500" size={18} />;
      default:
        return <Clock className="text-gray-500" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      case 'Shipped':
        return 'bg-blue-100 text-blue-800';
      case 'Processing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/store');
    }
  };

  const handleReturnClick = (order) => {
    setSelectedOrder(order);
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnReason.trim()) {
      toast.error('Please provide a reason for return');
      return;
    }

    setReturnLoading(true);
    try {
      await orderService.requestReturn(selectedOrder._id, returnReason);
      toast.success('Return request submitted successfully!');
      setShowReturnModal(false);
      setReturnReason('');
      fetchOrders(); // Refresh orders
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit return request');
    } finally {
      setReturnLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl font-bold text-purple-600">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-2 hover:bg-white/20 rounded-lg">
            <ChevronLeft size={24} />
          </button>
          <Package size={28} />
          <h1 className="text-xl font-bold">My Orders</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Package size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-4">You haven't placed any orders yet.</p>
            <button
              onClick={handleBack}
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order._id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Order Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm text-gray-500">Order ID</p>
                    <p className="font-semibold text-gray-800">#{order._id.slice(-8).toUpperCase()}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {order.status || 'Processing'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {getStatusIcon(order.status)}
                  <span>{formatDate(order.createdAt)}</span>
                </div>
              </div>

              {/* Order Items Preview */}
              <div className="p-4">
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {order.orderItems?.slice(0, 4).map((item, index) => (
                    <div key={index} className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={item.image || 'https://via.placeholder.com/100'}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {order.orderItems?.length > 4 && (
                    <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-sm text-gray-500">+{order.orderItems.length - 4}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center mt-3">
                  <p className="text-sm text-gray-500">
                    {order.orderItems?.length} {order.orderItems?.length === 1 ? 'item' : 'items'}
                  </p>
                  <p className="font-bold text-purple-600">
                    {formatPrice(order.totalPrice)}
                  </p>
                </div>

                {/* View Details Button */}
                <button
                  onClick={() => {
                    setExpandedOrder(expandedOrder === order._id ? null : order._id);
                  }}
                  className="w-full mt-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  {expandedOrder === order._id ? 'Hide Details' : 'View Details'}
                </button>

                {/* Expanded Details */}
                {expandedOrder === order._id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <h3 className="font-semibold text-gray-800 mb-3">Order Items</h3>
                    <div className="space-y-3">
                      {order.orderItems?.map((item, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={item.image || 'https://via.placeholder.com/100'}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                            <p className="text-sm font-medium text-purple-600">
                              {formatPrice(item.price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Shipping Address - Fixed with optional chaining */}
                    {order.shippingAddress && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-700 text-sm mb-1">Shipping Address</h4>
                        <p className="text-sm text-gray-500">
                          {order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.zipCode}, {order.shippingAddress?.country}
                        </p>
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Payment Method</p>
                        <p className="font-medium text-gray-800">{order.paymentMethod || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Payment Status</p>
                        <p className={`font-medium ${order.isPaid ? 'text-green-600' : 'text-yellow-600'}`}>
                          {order.isPaid ? 'Paid' : 'Pending'}
                        </p>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="text-gray-800">{formatPrice(order.itemsPrice)}</span>
                      </div>
                      {order.shippingPrice > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Shipping</span>
                          <span className="text-gray-800">{formatPrice(order.shippingPrice)}</span>
                        </div>
                      )}
                      {order.taxPrice > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Tax</span>
                          <span className="text-gray-800">{formatPrice(order.taxPrice)}</span>
                        </div>
                      )}
                      {order.couponDiscount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span>-{formatPrice(order.couponDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-purple-600 pt-2 border-t border-gray-100">
                        <span>Total</span>
                        <span>{formatPrice(order.totalPrice)}</span>
                      </div>
                    </div>

                    {/* Return Button - Only show for Delivered orders */}
                    {(order.status === 'Delivered' || order.status === 'delivered') && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {order.returnRequested ? (
                          <div className={`p-3 rounded-lg text-center ${
                            order.returnStatus === 'requested' ? 'bg-yellow-50 text-yellow-700' :
                            order.returnStatus === 'approved' ? 'bg-green-50 text-green-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            <p className="font-medium">
                              {order.returnStatus === 'requested' && 'Return Requested - Pending Approval'}
                              {order.returnStatus === 'approved' && 'Return Approved'}
                              {order.returnStatus === 'rejected' && 'Return Rejected'}
                            </p>
                            {order.returnReason && (
                              <p className="text-sm mt-1">Reason: {order.returnReason}</p>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleReturnClick(order)}
                            className="w-full py-2 px-4 bg-orange-100 text-orange-700 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-orange-200 transition-colors"
                          >
                            <RotateCcw size={18} />
                            Request Return
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Request Return</h2>
              <button 
                onClick={() => setShowReturnModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleReturnSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Return
                </label>
                <textarea
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Please describe why you want to return this order..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowReturnModal(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={returnLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50"
                >
                  {returnLoading ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;

